import { ChangeDetectionStrategy, Component, computed, inject, input, model, output } from '@angular/core';
import { DomSanitizer, SafeHtml } from '@angular/platform-browser';
import { TranslocoService } from '@ngneat/transloco';
import { SharedModule } from '@shared/shared.module';
import {
  HD_BODYGRAPH_COLOR_CONFIG_PRESETS,
  HD_BODYGRAPH_WARM_DUSK_SUBTLE_COLOR_CONFIG,
  HdBodygraphColorConfigPresetId,
} from '../../hd-bodygraph-color-config';
import {
  HdBodygraphIconKey,
  getHdBodygraphIconFaName,
  getHdGateIconFilterKeys,
} from '../../hd-bodygraph-icons';
import { HdBodygraphCenter } from '../../hd-bodygraph.models';
import { buildHdCenterShapeSvg } from '../../hd-center-shapes';
import { HdCheatSheetPaintValidation } from '../cheat-sheet-grid/hd-cheat-sheet-grid.component';
import { HdContentLanguageService } from '../../hd-content-language.service';
import { HdGateInfoService } from '../../hd-gate-info.service';

/** Centerknoppen in de bodygraph-volgorde van boven naar onder. */
const HD_CHEAT_SHEET_CENTER_FILTER_ORDER: readonly HdBodygraphCenter[] = [
  'hoofd', 'ajna', 'keel', 'zelf', 'hart', 'milt', 'sacraal', 'solarplexus', 'root',
];

interface HdCheatSheetCenterFilterOption {
  center: HdBodygraphCenter;
  label: string;
  /**
   * Mini-centericoon (dezelfde vorm als de center-mini-kaartjes) in de
   * centerkleur van het gekozen palet.
   */
  icon: SafeHtml;
}

/** Sizing van het mini-centericoon op de knop: hoogte volgt de knop-tekst. */
const HD_CHEAT_SHEET_CENTER_ICON_STYLE = 'display:block;width:auto;height:100%;overflow:visible';

/**
 * Controls-bar bovenaan de cheat-sheet pagina:
 * - taalkeuze (NL/EN) — schrijft naar {@link HdContentLanguageService} zodat het
 *   spel én de cheat-sheet card vertalingen in de gekozen taal tonen, zonder
 *   dat de globale app-taal mee verandert;
 * - 6 toggles om visuele info te verbergen (poortcijfers, kleuren+center, circuit-info
 *   in de onderbalk, kanaal+pp in de onderbalk, keynotes, body content) — werken via
 *   two-way `model()` zodat de pagina per-card reveal kan resetten op basis van
 *   flag-wijzigingen;
 * - dice-knop om de kaarten in een willekeurige volgorde te zetten;
 * - icoon-filtergroep ("Filter:"): één toggle per poort-eigenschap-icoon
 *   (druk, angsten, memory, …) — multi-select, de pagina toont enkel kaarten
 *   waarvan de icoon-lay-out minstens één geselecteerd icoon bevat;
 * - centerfiltergroep ("Center:"): één toggle per center met een kleurstip in
 *   de centerkleur van het gekozen palet — multi-select, de pagina combineert
 *   ze met de icoon-filters (EN tussen de groepen, OF binnen een groep);
 * - inkleur-spelmodus (verfemmer-knop): de pagina verbergt de kleuren, de
 *   centerknoppen kiezen dan één center als verfemmer en een klik op een kaart
 *   wordt een inkleurpoging (zie `HdCheatSheetGridComponent`).
 *
 * Self-contained presentational component zonder eigen state behalve de model
 * signals — alle "echte" logica (per-card override, shuffle) zit in de pagina.
 */
@Component({
  selector: 'hd-cheat-sheet-controls-bar',
  templateUrl: './hd-cheat-sheet-controls-bar.component.html',
  styleUrl: './hd-cheat-sheet-controls-bar.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [SharedModule],
})
export class HdCheatSheetControlsBarComponent {
  readonly hideGateNumber = model(false);
  readonly hideKeynotes = model(false);
  readonly hideColors = model(false);
  readonly hideFooterCircuit = model(false);
  readonly hideFooterChannel = model(false);
  readonly hideBody = model(false);
  readonly bodyView = model<'default' | 'lines'>('default');
  readonly colorConfigId = model<HdBodygraphColorConfigPresetId>('warm-dusk-subtle');
  /** Actieve icoon-filters (multi-select); leeg = geen filtering. */
  readonly iconFilters = model<readonly HdBodygraphIconKey[]>([]);
  /** Actieve centerfilters (multi-select); leeg = alle centers. */
  readonly centerFilters = model<readonly HdBodygraphCenter[]>([]);
  /** Inkleur-spelmodus: kleuren verborgen, centerknoppen kiezen de verfemmer. */
  readonly paintMode = model(false);
  /** Gekozen verfemmer-center in de inkleur-spelmodus (single-select). */
  readonly paintCenter = model<HdBodygraphCenter | null>(null);
  /** Uitkomst van de laatste validatie (pagina rekent ze uit op vraag van `validatePaintRequested`). */
  readonly paintValidation = input<HdCheatSheetPaintValidation | null>(null);

  readonly diceClicked = output<void>();
  /** Valideer-knop in de inkleurmodus: controleer of alle poorten van de verfemmer gevonden zijn. */
  readonly validatePaintRequested = output<void>();

  private readonly gateInfo = inject(HdGateInfoService);
  private readonly contentLang = inject(HdContentLanguageService);
  private readonly transloco = inject(TranslocoService);
  private readonly sanitizer = inject(DomSanitizer);

  /**
   * Vertaalde UI-tekst in de content-taal (zoals de configurator): de
   * laad-tick zorgt dat de tekst meekomt zodra de vertalingen geladen zijn.
   */
  protected t(key: string, params?: Record<string, unknown>): string {
    this.contentLang.translationsLoadedTick();
    return this.transloco.translate(`humanDesign.chart.cheatSheetControls.${key}`, params, this.contentLang.effectiveLang());
  }

  protected readonly colorConfigPresets = HD_BODYGRAPH_COLOR_CONFIG_PRESETS;
  protected readonly selectedColorConfigLabel = computed(
    () => this.colorConfigPresets.find(p => p.id === this.colorConfigId())?.label ?? 'Kleuren…',
  );

  protected readonly iconFilterKeys = getHdGateIconFilterKeys();

  /** Vertaalde icoon-keynotes (content-taal) als tooltips op de filterknoppen. */
  private readonly iconFilterLabels = computed(() => {
    this.contentLang.translationsLoadedTick();
    return this.gateInfo.iconLabelsIn(this.contentLang.effectiveLang());
  });

  /** Negen centerknoppen: vertaald label (content-taal) + mini-centericoon in de paletkleur. */
  protected readonly centerFilterOptions = computed<readonly HdCheatSheetCenterFilterOption[]>(() => {
    this.contentLang.translationsLoadedTick();
    const labels = this.gateInfo.centerLabelsIn(this.contentLang.effectiveLang());
    const colors = (this.colorConfigPresets.find(p => p.id === this.colorConfigId())?.config
      ?? HD_BODYGRAPH_WARM_DUSK_SUBTLE_COLOR_CONFIG).centerDefined;
    return HD_CHEAT_SHEET_CENTER_FILTER_ORDER.map(center => ({
      center,
      label: labels[center],
      icon: this.sanitizer.bypassSecurityTrustHtml(
        buildHdCenterShapeSvg(center, `fill="${colors[center]}"`, HD_CHEAT_SHEET_CENTER_ICON_STYLE),
      ),
    }));
  });

  protected toggleHideGateNumber(): void {
    this.hideGateNumber.update(v => !v);
  }

  protected toggleHideKeynotes(): void {
    this.hideKeynotes.update(v => !v);
  }

  protected toggleHideColors(): void {
    this.hideColors.update(v => !v);
  }

  protected toggleHideFooterCircuit(): void {
    this.hideFooterCircuit.update(v => !v);
  }

  protected toggleHideFooterChannel(): void {
    this.hideFooterChannel.update(v => !v);
  }

  protected toggleHideBody(): void {
    this.hideBody.update(v => !v);
  }

  protected toggleBodyView(): void {
    this.bodyView.update(v => v === 'default' ? 'lines' : 'default');
  }

  protected onDiceClick(): void {
    this.diceClicked.emit();
  }

  protected iconFilterFaName(key: HdBodygraphIconKey): string {
    return getHdBodygraphIconFaName(key);
  }

  protected iconFilterTooltip(key: HdBodygraphIconKey): string {
    return this.iconFilterLabels()[key] || key;
  }

  protected isIconFilterActive(key: HdBodygraphIconKey): boolean {
    return this.iconFilters().includes(key);
  }

  protected toggleIconFilter(key: HdBodygraphIconKey): void {
    this.iconFilters.update(filters => filters.includes(key)
      ? filters.filter(candidate => candidate !== key)
      : [...filters, key]);
  }

  /** In de spelmodus toont de knop de gekozen verfemmer, anders het filter. */
  protected isCenterFilterActive(center: HdBodygraphCenter): boolean {
    return this.paintMode() ? this.paintCenter() === center : this.centerFilters().includes(center);
  }

  protected toggleCenterFilter(center: HdBodygraphCenter): void {
    if (this.paintMode()) {
      this.paintCenter.update(current => current === center ? null : center);
      return;
    }
    this.centerFilters.update(filters => filters.includes(center)
      ? filters.filter(candidate => candidate !== center)
      : [...filters, center]);
  }

  /**
   * Spelmodus aan/uit. Aan = kleuren verbergen (de toggle blijft ook uit
   * staan na het verlaten, zodat je vlot heen en weer kunt); de gekozen
   * verfemmer blijft bewaard om verder te kleuren waar je was.
   */
  protected togglePaintMode(): void {
    const next = !this.paintMode();
    this.paintMode.set(next);
    if (next) {
      this.hideColors.set(true);
    }
  }

  /** Met een verfemmer valideert de knop die kleur, zonder verfemmer alle poorten. */
  protected onValidatePaint(): void {
    this.validatePaintRequested.emit();
  }

  /** Validatie-uitkomst voor de huidige verfemmer (of "alles"); een uitkomst van een ander center telt niet. */
  protected readonly currentPaintValidation = computed(() => {
    const validation = this.paintValidation();
    return validation != null && validation.center === this.paintCenter() ? validation : null;
  });

  protected onColorConfigChange(
    presetId: HdBodygraphColorConfigPresetId,
    dropdown: { toggleMenu: (visible?: boolean) => void },
  ): void {
    dropdown.toggleMenu(false);
    this.colorConfigId.set(presetId);
  }
}
